import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';

import type {
  TokenSearchResponse,
  TokensByNameOrSymbolQuery,
} from '../utils/0x/token-registry.types';
import { fetcher } from '../utils/fetcher';
import { routes } from '../utils/routes';
import { objectToQueryString } from '../utils/string';

const DEFAULT_RESULTS_LIMIT = 10;

type UseSearchTokensParams = {
  query?: string;
  chainIds?: number[];
  limit?: number;
  enabled?: boolean;
};

type UseSearchTokensArgs = UseSearchTokensParams & {
  effectiveLimit: number;
};

const buildUrl = ({ query, chainIds, effectiveLimit }: UseSearchTokensArgs, page: number) => {
  const parameters: TokensByNameOrSymbolQuery = {
    chainId: chainIds,
    limit: effectiveLimit,
    page, // 0x API uses 1-based pagination
    query: query ?? '',
  };

  return `${routes.api.SEARCH_TOKENS}${objectToQueryString(parameters)}`;
};

/** Identifies cached searches by query, chains and result limit. */
export const searchTokensQueryKey = ({ query, chainIds, limit }: UseSearchTokensParams) =>
  ['search-tokens', { query, chainIds, limit }] as const;

/** Loads keyword search results with cached pagination. */
export const useSearchTokens = ({
  query,
  chainIds,
  limit = DEFAULT_RESULTS_LIMIT,
  enabled = true,
}: UseSearchTokensParams): UseInfiniteQueryResult<
  { pages: TokenSearchResponse[]; pageParams: number[] },
  Error
> & { fetchNextPage: () => Promise<unknown> } => {
  return useInfiniteQuery<
    TokenSearchResponse,
    Error,
    { pages: TokenSearchResponse[]; pageParams: number[] },
    ReturnType<typeof searchTokensQueryKey>,
    number
  >({
    queryKey: searchTokensQueryKey({ query, chainIds, limit }),
    queryFn: (context) =>
      fetcher<TokenSearchResponse>(
        buildUrl({ query, chainIds, effectiveLimit: limit }, context.pageParam),
      ),
    initialPageParam: 1,
    // Bump the page number until the API returns fewer than `limit` items.
    getNextPageParam: (lastPage, allPages) => {
      if ('data' in lastPage && lastPage.data && lastPage.data.length >= limit) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: enabled && !!query?.trim(),
    staleTime: Infinity,
    refetchOnReconnect: false,
  });
};
